import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, Warehouse } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { DE_STATES } from "@logiful/domain";
import { api, ApiError, type WarehouseSiteItem } from "@/lib/api";
import { fmtRelative } from "@/lib/format";
import { Badge, Button, Card, Input } from "./ui";

const errMsg = (e: unknown) => (e instanceof ApiError ? ((e.body as { message?: string })?.message ?? "Fehler") : "Fehler");
const STATES = Object.entries(DE_STATES);

/**
 * Logiful intern: Lagerstandorte – Adresse kommt automatisch aus FFN (täglich und bei jedem Lagerabruf),
 * das Bundesland aus der PLZ. Eigene Lager lassen sich von Hand anlegen; das Bundesland ist abweichend festlegbar.
 */
export function WarehouseSitesCard({ admin }: { admin: boolean }) {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["admin-warehouse-sites"], queryFn: api.admin.warehouseSites });
  const [adding, setAdding] = useState(false);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ["admin-warehouse-sites"] }); void qc.invalidateQueries({ queryKey: ["calendar"] }); };
  const setRegion = useMutation({
    mutationFn: (p: { id: string; region: string | null }) => api.admin.updateWarehouseSite(p.id, { region: p.region }),
    onSuccess: () => { toast.success("Bundesland gespeichert"); refresh(); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const setCode = useMutation({
    mutationFn: (p: { id: string; code: string | null }) => api.admin.updateWarehouseSite(p.id, { code: p.code }),
    onSuccess: () => { toast.success("Kürzel gespeichert"); refresh(); },
    onError: (e) => toast.error("Nicht gespeichert", { description: errMsg(e) }),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.admin.deleteWarehouseSite(id),
    onSuccess: () => { toast.success("Standort gelöscht"); refresh(); },
    onError: (e) => toast.error("Nicht gelöscht", { description: errMsg(e) }),
  });
  return (
    <Card className="mt-4 p-4 space-y-3">
      <div className="flex items-start gap-3">
        <Warehouse className="size-4 text-fg-subtle mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="font-medium text-[13.5px]">Lagerstandorte</p>
          <p className="text-[12.5px] text-fg-muted">Adressen kommen automatisch aus FFN, das Bundesland aus der PLZ – bestimmt die Feiertage im Kalender der Kunden. Bei Orten an einer Landesgrenze das Bundesland festlegen. Das Kürzel erscheint bei Kunden in der Auftragsliste und im Auftrag.</p>
        </div>
        {admin && <Button size="sm" icon={<Plus className="size-3.5" />} onClick={() => setAdding(true)}>Lager anlegen</Button>}
      </div>
      {adding && <NewSite onDone={() => { setAdding(false); refresh(); }} onCancel={() => setAdding(false)} />}
      <ul className="divide-y divide-border rounded-xl border border-border">
        {q.data?.map((s) => (
          <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 px-3 py-2.5 text-[13px]">
            <div className="min-w-0 flex-1 basis-full sm:basis-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-medium">{s.name}</span>
                <span className="font-mono text-[11px] text-fg-subtle">{s.id}</span>
                {s.source === "ffn" && <Badge>aus FFN</Badge>}
                {s.source === "manual" && <Badge tone="info">von Hand</Badge>}
                {s.source === "unknown" && <Badge tone="warning">Adresse noch unbekannt</Badge>}
                {s.customers > 0 && <span className="text-[11.5px] text-fg-subtle">{s.customers} Kunde{s.customers > 1 ? "n" : ""}</span>}
              </div>
              <div className="text-[12px] text-fg-muted">
                {s.zip || s.city ? [s.street, [s.zip, s.city].filter(Boolean).join(" "), s.country].filter(Boolean).join(", ") : "Kommt mit dem nächsten FFN-Abgleich (nachts)."}
                {s.syncedAt && <span className="text-fg-subtle"> · abgeglichen {fmtRelative(s.syncedAt)}</span>}
              </div>
            </div>
            {/* Lagerkürzel: erscheint in Auftragsliste und -detail; leer = Lager-ID */}
            <Input
              key={`${s.id}:${s.code ?? ""}`}
              defaultValue={s.code ?? ""}
              disabled={!admin || setCode.isPending}
              placeholder={s.autoCode ?? "Kürzel"}
              maxLength={6}
              aria-label={`Kürzel für ${s.name}`}
              title="Lagerkürzel (2–6 Zeichen) – erscheint in Auftragsliste und Auftrag; leer = automatisch aus Name bzw. Ort"
              className="h-8 w-24 font-mono uppercase text-[12.5px]"
              onBlur={(e) => { const v = e.target.value.trim().toUpperCase() || null; if (v !== (s.code ?? null)) setCode.mutate({ id: s.id, code: v }); }}
              onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
            />
            <select
              disabled={!admin || setRegion.isPending}
              value={s.region ?? ""}
              onChange={(e) => setRegion.mutate({ id: s.id, region: e.target.value || null })}
              aria-label={`Bundesland für ${s.name}`}
              className="h-8 rounded-lg border border-border bg-surface px-2 text-[12.5px] max-w-[240px]"
            >
              <option value="">{s.autoRegion ? `Automatisch: ${DE_STATES[s.autoRegion as keyof typeof DE_STATES]}` : "Bundesland wählen …"}</option>
              {STATES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
            </select>
            {!s.effective && <Badge tone="warning">keine Feiertage</Badge>}
            {admin && s.manual && (
              <button onClick={() => { if (confirm(`Standort „${s.name}“ löschen?`)) del.mutate(s.id); }} className="size-8 grid place-items-center rounded-md text-fg-subtle hover:text-danger" aria-label={`${s.name} löschen`}><Trash2 className="size-3.5" /></button>
            )}
          </li>
        ))}
        {q.data && !q.data.length && <li className="px-3 py-4 text-[13px] text-fg-muted">Noch keine Lager bekannt – sie erscheinen, sobald ein Kunde mit FFN verbunden ist.</li>}
      </ul>
    </Card>
  );
}

function NewSite({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const [v, setV] = useState({ warehouseId: "", name: "", street: "", zip: "", city: "", country: "DE", region: "" });
  const save = useMutation({
    mutationFn: () => api.admin.createWarehouseSite({ ...(v.warehouseId.trim() ? { warehouseId: v.warehouseId.trim() } : {}), name: v.name, street: v.street || null, zip: v.zip || null, city: v.city || null, country: v.country, region: v.region || null }),
    onSuccess: () => { toast.success("Lager angelegt"); onDone(); },
    onError: (e) => toast.error("Nicht angelegt", { description: errMsg(e) }),
  });
  return (
    <div className="rounded-xl border border-border bg-surface-2/40 p-3 space-y-2">
      <div className="grid sm:grid-cols-2 gap-2">
        <Input value={v.name} placeholder="Name, z. B. Logiful Hamburg" onChange={(e) => setV({ ...v, name: e.target.value })} />
        <Input value={v.warehouseId} placeholder="FFN-Lager-ID (falls vorhanden)" onChange={(e) => setV({ ...v, warehouseId: e.target.value })} />
        <Input value={v.street} placeholder="Straße und Hausnummer" onChange={(e) => setV({ ...v, street: e.target.value })} className="sm:col-span-2" />
        <div className="grid grid-cols-[90px_1fr_60px] gap-2 sm:col-span-2">
          <Input value={v.zip} placeholder="PLZ" onChange={(e) => setV({ ...v, zip: e.target.value })} />
          <Input value={v.city} placeholder="Ort" onChange={(e) => setV({ ...v, city: e.target.value })} />
          <Input value={v.country} placeholder="DE" onChange={(e) => setV({ ...v, country: e.target.value.toUpperCase().slice(0, 2) })} />
        </div>
        <select value={v.region} onChange={(e) => setV({ ...v, region: e.target.value })} aria-label="Bundesland" className="h-8 rounded-lg border border-border bg-surface px-2 text-[13px]">
          <option value="">Bundesland automatisch aus PLZ</option>
          {STATES.map(([k, n]) => <option key={k} value={k}>{n}</option>)}
        </select>
      </div>
      <p className="text-[11.5px] text-fg-subtle">Mit FFN-Lager-ID gilt der Standort für alle Kunden, die dieses Lager nutzen – die Adresse wird dann nicht vom FFN-Abgleich überschrieben.</p>
      <div className="flex justify-end gap-2">
        <Button size="sm" onClick={onCancel}>Abbrechen</Button>
        <Button size="sm" variant="primary" disabled={v.name.trim().length < 2} loading={save.isPending} onClick={() => save.mutate()}>Anlegen</Button>
      </div>
    </div>
  );
}
